import {Component, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import {ColDef, GridApi, GridReadyEvent} from "ag-grid-community";
import {FormatCurrencyPipe} from "../../../pipes/format-currency.pipe";
import {ErrorHandlingService} from "../../../services/error-handling.service";
import {AgGridAngular} from "ag-grid-angular";
import {LoadingCardContentComponent} from "../../../shared/loading-card-content/loading-card-content.component";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {ICharityBalance} from "../../../interfaces/charity/ICharityBalance";
import {CharityPayoutService} from "../../../services/charity-payout.service";
import {IGameQueryResult} from "../../../interfaces/IGameQueryResult";
import {MatCard, MatCardContent, MatCardHeader, MatCardTitle} from "@angular/material/card";
import {MatButton} from "@angular/material/button";
import {PipesModule} from "../../../pipes/pipes.module";
import {
  CharityPayoutActionsComponent
} from "../../../components/charity/charity-payout-actions/charity-payout-actions.component";
import {PermissionTypes} from "../../../enum/PermissionTypes";
import {PermissionsService} from "../../../services/permissions.service";
import {
  TableExportButtonsComponent
} from "../../../components/table-export-buttons/table-export-buttons.component";
import {ITableExportRequest, TableExportService} from "../../../services/table-export.service";

@Component({
  selector: 'app-charity-balances-summary',
  standalone: true,
  templateUrl: './charity-balances-summary.component.html',
  imports: [
    AgGridAngular,
    LoadingCardContentComponent,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardContent,
    MatButton,
    PipesModule,
    TableExportButtonsComponent
  ],
  styleUrls: ['./charity-balances-summary.component.scss']
})
export class CharityBalancesSummaryComponent implements OnInit {

  private currencyPipe: FormatCurrencyPipe = inject(FormatCurrencyPipe);
  private charityPayoutService: CharityPayoutService = inject(CharityPayoutService);
  private errorHandlingService: ErrorHandlingService = inject(ErrorHandlingService);
  private permissionService: PermissionsService = inject(PermissionsService);
  private tableExportService: TableExportService = inject(TableExportService);

  @Input() set gameData(gameP: IGameQueryResult) {
    this.activeGame = gameP;
    this.fetchBalances();
  }

  /**
   * The row's charity, id and name. The name travels with it so the payout screen's picker can show the
   * charity straight away instead of waiting on its option list (#1505).
   */
  @Output() makePayoutClick: EventEmitter<{ id: string; name: string }> = new EventEmitter<{ id: string; name: string }>();

  public balancesGridApi!: GridApi<ICharityBalance>;
  public activeGame: IGameQueryResult | null = null;
  public charityBalances: ICharityBalance[] = [];
  public totalOwed: number = 0;
  public balanceColDefs: ColDef[] = [];

  uiState = UIStateEnum.ShowData;
  protected readonly UIStateEnum = UIStateEnum;

  ngOnInit(): void {
    this.initializeColumnDefs();
  }

  private initializeColumnDefs(): void {
    const baseColumns: ColDef[] = [
      {
        headerName: 'Charity Name',
        field: 'CharityName',
        resizable: true,
        sortable: true,
        flex: 2
      },
      {
        headerName: 'Total Contributions',
        field: 'TotalContributions',
        resizable: true,
        sortable: true,
        flex: 1,
        valueFormatter: params => {
          return this.currencyPipe.transform(params.value);
        }
      },
      {
        headerName: 'Total Payouts',
        field: 'TotalPayouts',
        resizable: true,
        sortable: true,
        flex: 1,
        valueFormatter: params => {
          return this.currencyPipe.transform(params.value);
        }
      },
      {
        // Shown next to payouts so a balance that moved without a payout has a visible cause: without this
        // column an admin sees "owed" change and has nothing to reconcile it against.
        headerName: 'Adjustments',
        field: 'TotalAdjustments',
        resizable: true,
        sortable: true,
        flex: 1,
        valueFormatter: params => {
          return this.currencyPipe.transform(params.value);
        }
      },
      {
        headerName: 'Amount Owed',
        field: 'AmountOwed',
        resizable: true,
        sortable: true,
        sort: 'desc',
        flex: 1,
        valueFormatter: params => {
          return this.currencyPipe.transform(params.value);
        },
        cellStyle: params => {
          if (params.value > 0) {
            return {color: '#2e7d32', fontWeight: 'bold'};
          }
          return null;
        }
      }
    ];

    // Only add Actions column if user has ModifyCharityPayouts permission
    const hasModifyPermission = this.permissionService.userHasPermission(PermissionTypes.ModifyCharityPayouts);

    if (hasModifyPermission) {
      baseColumns.push({
        headerName: 'Actions',
        field: 'CharityId',
        resizable: true,
        flex: 1,
        cellRenderer: CharityPayoutActionsComponent,
        cellRendererParams: {
          onMakePayoutClick: (charityId: string, charityName: string) => {
            this.makePayoutClick.emit({ id: charityId, name: charityName });
          }
        }
      });
    }

    this.balanceColDefs = baseColumns;
  }

  fetchBalances(): void {
    if (!this.activeGame) return;

    this.uiState = UIStateEnum.ShowLoading;
    this.charityPayoutService.fetchBalancesByGameId(this.activeGame.Id).subscribe({
      next: (balances) => {
        this.charityBalances = balances;
        this.calculateTotalOwed(balances);
        this.uiState = UIStateEnum.ShowData;
      },
      error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  calculateTotalOwed(balances: ICharityBalance[]): void {
    this.totalOwed = balances.reduce((sum, balance) => sum + balance.AmountOwed, 0);
  }

  /**
   * The balances table as shown, with the same total underneath it. The Actions column is left out on purpose:
   * a "Make payout" button is not information, and a column of blanks in a spreadsheet invites the question of
   * what is missing from it.
   */
  private buildExportRequest(): ITableExportRequest<ICharityBalance> {
    return {
      title: 'Charity balances',
      subtitle: this.activeGame?.Name ?? '',
      summary: [
        {label: 'Charities', value: `${this.charityBalances.length}`},
        {label: 'Total owed to all charities', value: this.currencyPipe.transform(this.totalOwed)}
      ],
      rows: this.charityBalances,
      // Every money column exports as a plain number: this is the table someone opens in Excel to check that
      // earned + adjustments − paid comes to what is owed, and that arithmetic needs numbers.
      columns: [
        {header: 'Charity Name', value: balanceP => balanceP.CharityName},
        {
          header: 'Total Contributions',
          value: balanceP => this.currencyPipe.transform(balanceP.TotalContributions),
          csvValue: balanceP => this.tableExportService.csvMoney(balanceP.TotalContributions),
          numeric: true
        },
        {
          header: 'Total Payouts',
          value: balanceP => this.currencyPipe.transform(balanceP.TotalPayouts),
          csvValue: balanceP => this.tableExportService.csvMoney(balanceP.TotalPayouts),
          numeric: true
        },
        {
          header: 'Adjustments',
          value: balanceP => this.currencyPipe.transform(balanceP.TotalAdjustments),
          csvValue: balanceP => this.tableExportService.csvMoney(balanceP.TotalAdjustments),
          numeric: true
        },
        {
          header: 'Amount Owed',
          value: balanceP => this.currencyPipe.transform(balanceP.AmountOwed),
          csvValue: balanceP => this.tableExportService.csvMoney(balanceP.AmountOwed),
          numeric: true
        }
      ],
      fileName: this.tableExportService.fileNameFor([
        this.activeGame?.ShortName || this.activeGame?.Name,
        'charity-balances'
      ])
    };
  }

  onExportCsv(): void {
    this.tableExportService.exportCsv(this.buildExportRequest());
  }

  onExportPdf(): void {
    this.tableExportService.exportPdf(this.buildExportRequest());
  }

  onBalancesGridReady(params: GridReadyEvent<ICharityBalance>) {
    this.balancesGridApi = params.api;
    this.balancesGridApi.sizeColumnsToFit({
      defaultMinWidth: 100,
    });
  }
}
